import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { RadioButton } from '@/components/RadioButton/RadioButton';

import { FilterValues } from '@/domains/Groups/constants/FilterValues';
import { SORT_OPTIONS } from '@/domains/Groups/constants/SortOptions';
import { SortValues } from '@/domains/Groups/constants/SortValues';
import {
  DesktopWrapper,
  Fieldset,
  Legend,
  MobileWrapper,
  SortButton,
  SortButtons,
} from './FilterSection.styles';
import { FILTER_OPTIONS } from '../../constants/FilterOptions';

export enum EFilters {
  sort = 1,
  filter,
}

export function FilterSection({
  onFilterChange,
  onSortChange,
  filteredBy,
  sortedBy,
  mobile,
  filtersOpenedMob,
}: {
  onFilterChange: (filteredBy: FilterValues) => void;
  onSortChange: (sortedBy: SortValues) => void;
  filteredBy: Array<FilterValues>;
  sortedBy: SortValues;
  mobile: boolean;
  filtersOpenedMob?: EFilters;
}): JSX.Element {
  const theme = useTheme();

  function handleSortChange(event) {
    const { value } = event?.target ?? {};
    onSortChange(value || event);
  }

  function handleFilterChange(value) {
    onFilterChange(value);
  }
  return mobile ? (
    !!filtersOpenedMob && (
      <MobileWrapper>
        {filtersOpenedMob === EFilters.sort && (
          <Fieldset>
            <Legend>Sort by:</Legend>
            {SORT_OPTIONS.map(({ label, value }) => (
              <RadioButton
                radioColor="#5FA3A9"
                key={value}
                id={value}
                name="sort"
                value={value}
                label={label}
                onChange={handleSortChange}
                checked={sortedBy === value}
              />
            ))}
          </Fieldset>
        )}
        {filtersOpenedMob === EFilters.filter && (
          <Fieldset>
            <Legend>Show:</Legend>
            <Box display="flex" flexDirection="column">
              {FILTER_OPTIONS.map(({ label, value, icon }) => (
                <Box
                  sx={{ cursor: 'pointer' }}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  mb={2}
                  key={label}
                  onClick={() => handleFilterChange(value)}
                >
                  <Box
                    sx={{
                      color: filteredBy.includes(value) ? '#5FA3A9' : '#B0B2BB',
                    }}
                  >
                    {icon}
                  </Box>

                  <Box
                    sx={{
                      fontWeight: filteredBy.includes(value) ? '600' : '100',
                      color: filteredBy.includes(value) ? '#0A444D' : '#52545F',
                    }}
                  >
                    {label}
                  </Box>
                </Box>
              ))}
            </Box>
          </Fieldset>
        )}
      </MobileWrapper>
    )
  ) : (
    <DesktopWrapper>
      <Stack
        direction="row"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={theme.spacing(5)}
      >
        <SortButtons direction="row">
          {SORT_OPTIONS.map(({ label, value }) => (
            <SortButton
              key={value}
              active={sortedBy === value}
              onClick={() => handleSortChange(value)}
            >
              {label}
            </SortButton>
          ))}
        </SortButtons>
        <Stack
          direction="row"
          flexWrap="nowrap"
          alignItems="center"
          spacing={5}
        >
          <Typography
            variant="body2"
            color={theme.palette.grey[400]}
            fontSize={theme.spacing(4.5)}
          >
            Filter:
          </Typography>
          {FILTER_OPTIONS.map(({ label, value, icon }) => (
            <Tooltip key={label} title={label}>
              <Box
                sx={{
                  cursor: 'pointer',
                  color: filteredBy.includes(value) ? '#5FA3A9' : '#B0B2BB',
                }}
                onClick={() => handleFilterChange(value)}
              >
                {icon}
              </Box>
            </Tooltip>
          ))}
        </Stack>
      </Stack>
    </DesktopWrapper>
  );
}
